"use client";

import React, { useEffect, useState } from "react";
import { useMarkupStore } from "../store";
import type { UserInfo, UserRole } from "../types";
import { listTeamMembers, updateUserRole } from "../services/userProfile";
import { ALLOWED_EMAIL_DOMAIN } from "../config/access";
import { ArrowLeftIcon } from "./Icons";

const ROLES: { value: UserRole; label: string }[] = [
    { value: "default", label: "Member" },
    { value: "developer", label: "Developer" },
    { value: "admin", label: "Admin" },
];

/**
 * Admin-only: who's using the tool, and their roles.
 *
 * Anyone at Matter can join by signing up with their (verified) company email,
 * so there's no inviting to do. Members raise, discuss and move tickets
 * along; developers and admins can also edit or delete anyone's; admins
 * manage roles.
 */
export const TeamPanel: React.FC<{ onBack: () => void }> = ({ onBack }) => {
    const currentUser = useMarkupStore((s) => s.currentUser);
    const [members, setMembers] = useState<UserInfo[]>([]);
    const [error, setError] = useState<string | null>(null);

    const refresh = async () => {
        try {
            setMembers(await listTeamMembers());
        } catch (_) {
            setError("Couldn’t load the team. Please try again.");
        }
    };

    useEffect(() => {
        void refresh();
    }, []);

    const handleRole = async (member: UserInfo, next: UserRole) => {
        setError(null);
        try {
            await updateUserRole(member.id, next);
            await refresh();
        } catch (_) {
            setError("Couldn’t change that role.");
        }
    };

    return (
        <div className="flex-1 overflow-y-auto p-5">
            <button
                type="button"
                onClick={onBack}
                className="flex items-center gap-1.5 text-sm! text-gray-600 hover:text-gray-900 mb-4"
            >
                <ArrowLeftIcon className="w-4 h-4" />
                Back
            </button>

            <h2 className="text-base! font-semibold text-gray-900 mb-1">Team</h2>
            <p className="text-xs! text-gray-500 mb-4">
                Anyone with a verified @{ALLOWED_EMAIL_DOMAIN} email can sign up and
                join as a member. Developers and admins can also edit or delete
                anyone’s tickets; admins manage roles.
            </p>

            {error && (
                <p role="alert" className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs!">
                    {error}
                </p>
            )}

            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl">
                {members.map((member) => (
                    <li key={member.id} className="flex items-center gap-2 px-3 py-2">
                        <span className="flex-1 min-w-0">
                            <span className="block truncate text-sm! text-gray-800">{member.name}</span>
                            <span className="block truncate text-[11px] text-gray-500">{member.email}</span>
                        </span>
                        <select
                            aria-label={`Role for ${member.name}`}
                            value={member.role}
                            // Changing your own role could lock you out of this screen.
                            disabled={member.id === currentUser?.id}
                            onChange={(e) => handleRole(member, e.target.value as UserRole)}
                            className="px-2 py-1 border border-gray-200 rounded-lg text-xs! disabled:opacity-60"
                        >
                            {ROLES.map((r) => (
                                <option key={r.value} value={r.value}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </li>
                ))}
            </ul>
        </div>
    );
};
